import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Step } from "./invoice-form/constants";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { Plus, QrCode, PencilSimple, File } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useGetCurrencies } from "@/services/queries/currency";
import { useQueryClient } from "@tanstack/react-query";
import { formatDate, toDateOnlyString } from "@/lib/utils";
import useVessel from "@/store/use-vessel";
import { Mixpanel } from "@/lib/mixpanel";
import useAuth from "@/store/use-auth";
import { getActiveOrganizationId } from "@/lib/api";
import { DocumentUploadModal } from "@/components/document-upload/document-upload-modal";
import { Button } from "@/components/ui/button";
import { useCreateVendor } from "@/services/mutations/vendor";
import { useGetVendors } from "@/services/queries/vendor";
import { useNavigate } from "react-router-dom";
import { useGetVessels } from "@/services/queries/vessel";
import InvoiceForm, {
  invoiceFormSchema,
  InvoiceFormValues,
} from "./invoice-form";
import {
  useCreateInvoice,
  useCreateInvoiceAttachment,
} from "@/services/mutations/invoices";
import { startOfToday } from "date-fns";

const AddInvoiceButton = ({ defaultDate }: { defaultDate?: Date }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<number>(Step.INITAL);
  const [open, setOpen] = useState(false);
  const [showOptionsDrawer, setShowOptionsDrawer] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const { selectedVessel, setSelectedVessel } = useVessel();
  const [scannedInvoice, setScannedInvoice] = useState<File | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { mutateAsync: createInvoiceAttachment } = useCreateInvoiceAttachment();

  // New invoices default to the day picked in the header, else today. Read on
  // open, not memoized: the PWA can stay open past midnight.
  const pickedDay = () => defaultDate ?? startOfToday();

  const defaultValues = useMemo(
    () => ({
      budgetId: "",
      vendor: null,
      currencyId: "",
      issueDate: defaultDate ?? startOfToday(),
      dueDate: defaultDate ?? startOfToday(),
      files: [],
      description: "",
      vesselId: selectedVessel?.id ?? "",
      lineItems: [],
      reference: "",
      invoiceNumber: "",
      status: "Draft",
      conversionRate: undefined,
      vendorPaymentMethods: [],
    }),
    [selectedVessel, defaultDate]
  );

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues,
  });

  const { data: currenciesData } = useGetCurrencies();
  const { data: vendorsData } = useGetVendors();
  const { data: vesselsData } = useGetVessels();

  const currencies = useMemo(() => {
    return currenciesData?.value ?? [];
  }, [currenciesData?.value]);

  const vendors = useMemo(() => {
    return vendorsData?.value ?? [];
  }, [vendorsData?.value]);

  const vessels = useMemo(() => {
    return vesselsData?.value ?? [];
  }, [vesselsData?.value]);

  const handleScanInvoice = () => {
    setShowOptionsDrawer(false);

    // Create file input element for file selection
    const fileInput = document.createElement("input");
    fileInput.setAttribute("data-testid", "scan-invoice-file-input");
    fileInput.type = "file";
    fileInput.accept = "image/*,application/pdf";
    fileInput.capture = "environment"; // Prefer rear camera on mobile devices
    fileInput.multiple = false;
    fileInput.style.position = "fixed";
    fileInput.style.top = "-9999px";
    fileInput.style.left = "-9999px";

    // Append to DOM
    document.body.appendChild(fileInput);

    fileInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // Process the selected file
        processInvoiceImage(file);
      }

      // Clean up
      document.body.removeChild(fileInput);
    };

    // Delay for closing the drawer and opening the file document upload modal
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fileInput.click();
      });
    });
  };

  const processInvoiceImage = (file: File) => {
    setShowDocumentUpload(true);
    setScannedInvoice(file);
  };

  const handleManualEntry = () => {
    setShowOptionsDrawer(false);
    // The form's dates are only seeded on mount and on close, and the header
    // day can change while it's closed without remounting this button.
    const day = pickedDay();
    form.setValue("issueDate", day);
    form.setValue("dueDate", day);
    setOpen(true);
  };

  // Hard guard against double-submit: a synchronous ref blocks duplicate requests even when rapid taps fire before React re-renders. Cleared only when the whole flow settles.
  const isSubmittingRef = useRef(false);

  const { mutate: addInvoice, isPending: isCreatingInvoice } = useCreateInvoice(
    {
      onSuccess: async ({ data }) => {
        if (form.getValues().files && form.getValues().files.length > 0) {
          // Uploads use allSettled (not Promise.all over fire-and-forget mutate): the
          // invoice was already saved, so a failed attachment must NOT reject
          // onSuccess (that would route to onError, keep the drawer open, and risk a
          // duplicate re-submit). We await all uploads, then surface any failure.
          const attachmentResults = await Promise.allSettled(
            form.getValues().files.map((file) =>
              createInvoiceAttachment({
                invoiceId: data.value.id,
                file: file as File,
              })
            )
          );
          if (attachmentResults.some((r) => r.status === "rejected")) {
            console.error(
              "Attachment upload failed",
              attachmentResults.filter((r) => r.status === "rejected")
            );
            toast({
              variant: "destructive",
              title: t("error_message.upload_failed"),
              description: t("error_message.upload_failed_description"),
            });
          }
        }

        queryClient.invalidateQueries({
          queryKey: ["invoices"],
        });

        const vessel = vessels.find(
          (vessel) => vessel.id === form.watch("vesselId")
        );

        if (vessel) {
          setSelectedVessel(vessel);
          navigate(
            `/?date=${formatDate(form.watch("issueDate") || new Date(), "yyyy-MM-dd")}`
          );
        }

        setOpen(false);
        form.reset();
        toast({
          title: t("success_message.add_invoice_title"),
          description: t("success_message.add_invoice_description"),
        });

        Mixpanel.track("Add Invoice Successfully");
        isSubmittingRef.current = false;
      },
      onError: (error) => {
        console.error(error);
        Mixpanel.track("Add Invoice Failed");
        isSubmittingRef.current = false;
      },
    }
  );

  const { mutate: createVendor, isPending: isCreatingVendor } = useCreateVendor(
    {
      onSuccess: ({ value }) => {
        Mixpanel.track("Vendor Addition Success When Adding Invoice");
        // Keep isSubmittingRef true — the invoice mutation's own handlers clear it.
        handleAddInvoice(form.getValues(), value.id);
      },
      onError: (error) => {
        console.error(error);
        Mixpanel.track("Vendor Addition Failed When Adding Invoice");
        isSubmittingRef.current = false;
      },
    }
  );

  const onSubmit = (values: InvoiceFormValues) => {
    // Block re-entry while a submission is already in flight.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (typeof values.vendor === "object" && values.vendor !== null) {
      handleAddInvoice(values, values.vendor.id);
    } else if (typeof values.vendor === "string") {
      const existingVendor = vendors.find(
        (vendor) =>
          vendor.name.toLowerCase() === (values.vendor as string).toLowerCase()
      );

      if (existingVendor) {
        handleAddInvoice(values, existingVendor.id);
      } else {
        createVendor({
          name: values.vendor,
          paymentMethods: (values.vendorPaymentMethods ?? []).map((item) => {
            return {
              ...item,
              name: values.vendor as string,
            };
          }),
        });
      }
    }
  };

  const handleAddInvoice = (values: InvoiceFormValues, vendorId: string) => {
    addInvoice({
      invoiceNumber: values.invoiceNumber,
      totalAmount: values.lineItems.reduce(
        (acc, lineItem) => acc + lineItem.amount,
        0
      ),
      currencyId: values.currencyId,
      description: values.description ?? "",
      issueDate: toDateOnlyString(values.issueDate),
      dueDate: toDateOnlyString(values.dueDate),
      reference: values.reference ?? "",
      organizationId: getActiveOrganizationId() ?? user?.org_id ?? "",
      budgetId: values.budgetId ?? "",
      vesselId: values.vesselId,
      status: values.status ?? "Draft",
      conversionRate: values.conversionRate,
      vendorId: vendorId ?? "",
      lineItems: values.lineItems.map((lineItem) => ({
        ...lineItem,
        budgetCategoryId: lineItem.budgetCategoryId ?? undefined,
        budgetSubcategoryId: lineItem.budgetSubcategoryId ?? undefined,
        budgetLineItemId: lineItem.budgetLineItemId ?? undefined,
      })),
    });
  };

  useEffect(() => {
    if (!showDocumentUpload) {
      setScannedInvoice(null);
    }
  }, [showDocumentUpload]);

  return (
    <>
      <Drawer open={showOptionsDrawer} onOpenChange={setShowOptionsDrawer}>
        <DrawerTrigger asChild>
          <Button
            data-testid="add-invoice-button"
            type="button"
            className="h-8 gap-1.5 px-2 py-0"
          >
            <Plus className="size-4" />
            <span className="text-sm font-medium">
              {t("button.add_invoice")}
            </span>
          </Button>
        </DrawerTrigger>
        <DrawerContent
          data-testid="add-invoice-options-drawer"
          className="h-auto"
        >
          <h2 className="mx-auto mb-6 max-w-[200px] text-center text-lg text-white">
            {t("title.add_invoice").toLocaleUpperCase(i18n.language)}
          </h2>
          <div className="flex flex-col gap-3">
            <button
              data-testid="scan-invoice-button"
              onClick={handleScanInvoice}
              className="flex w-full items-center justify-start gap-3 rounded-lg border border-white/20 px-4 py-4 text-base text-white"
            >
              <QrCode size={24} />
              <span>{t("button.scan_receipt")}</span>
            </button>

            <button
              data-testid="upload-document-button"
              onClick={() => {
                setShowOptionsDrawer(false);
                setShowDocumentUpload(true);
              }}
              className="flex w-full items-center justify-start gap-3 rounded-lg border border-white/20 px-4 py-4 text-base text-white"
            >
              <File size={24} />
              <span>{t("button.upload_file")}</span>
            </button>

            <button
              data-testid="manual-entry-button"
              onClick={handleManualEntry}
              className="flex w-full items-center justify-start gap-3 rounded-lg border border-white/20 px-4 py-4 text-base text-white"
            >
              <PencilSimple size={24} />
              <span>{t("button.enter_manually")}</span>
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {showDocumentUpload && (
        <DocumentUploadModal
          open={showDocumentUpload}
          onOpenChange={setShowDocumentUpload}
          onViewDocument={({ files, documentResult }) => {
            setStep(Step.INITAL);
            setShowDocumentUpload(false);

            const vendor = vendors.find(
              (vendor) => vendor.name === documentResult.vendorName
            );

            form.reset({
              ...form.getValues(),
              files: files,
              vendor: vendor ? { id: vendor.id } : documentResult.vendorName,
              reference: documentResult.reference,
              issueDate: documentResult.documentDate
                ? new Date(documentResult.documentDate)
                : pickedDay(),
              dueDate: documentResult.documentDate
                ? new Date(documentResult.documentDate)
                : pickedDay(),
              currencyId:
                currencies.find(
                  (currency) => currency.code === documentResult.currencyCode
                )?.id ?? "",
              lineItems: documentResult.lineItems.map((item) => ({
                ...item,
                budgetSubcategoryId: "",
                vatRate: item.taxRate,
                vatAmount: item.taxAmount,
              })),
              vendorPaymentMethods: [],
            });

            setOpen(true);
          }}
          initialFiles={scannedInvoice ? [scannedInvoice] : undefined}
        />
      )}

      <InvoiceForm
        open={open}
        setOpen={setOpen}
        form={form}
        step={step}
        setStep={setStep}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        isSubmitting={isCreatingInvoice || isCreatingVendor}
        initialVesselId={selectedVessel?.id}
      />
    </>
  );
};

export default AddInvoiceButton;
